/**
 * The Compliance Rail setting: whether the ribbon shows on EMR pages. One
 * value in chrome.storage.sync ('complianceRail'), read by the background
 * worker (getFreeComplianceStatus) and watched by the rail itself, which
 * shows or hides the moment it changes. Extension only: the web app has no
 * rail, so everything here is a no-op there.
 *
 * Unset follows the worker's rule: on for installs stamped at install time
 * (extensionInstalledAt), off for older ones.
 */
export const RAIL_SETTING_KEY = 'complianceRail';
const INSTALLED_AT_KEY = 'extensionInstalledAt';

function storage() {
  try {
    // eslint-disable-next-line no-undef
    const s = typeof chrome !== 'undefined' && chrome.runtime?.id ? chrome.storage : null;
    return s?.sync && s?.local ? s : null;
  } catch (e) {
    return null;
  }
}

/** Whether there is a rail to set (the extension). */
export const railSettingAvailable = () => Boolean(storage());

/** Calls back with true/false, as the worker would decide it. */
export function readRailSetting(callback) {
  const s = storage();
  if (!s) return;
  s.sync.get(RAIL_SETTING_KEY, (result) => {
    if (typeof result?.[RAIL_SETTING_KEY] === 'boolean') {
      callback(result[RAIL_SETTING_KEY]);
      return;
    }
    s.local.get(INSTALLED_AT_KEY, (local) => callback(Boolean(local?.[INSTALLED_AT_KEY])));
  });
}

export function writeRailSetting(on) {
  try {
    storage()?.sync.set({ [RAIL_SETTING_KEY]: Boolean(on) });
  } catch (e) {
    /* unreachable storage: the setting stays as it was */
  }
}

/** Changes from anywhere (Note Preferences, the rail's own menu). Returns an unsubscribe. */
export function subscribeRailSetting(callback) {
  const s = storage();
  if (!s?.onChanged) return () => {};
  const listener = (changes, area) => {
    if (area === 'sync' && changes[RAIL_SETTING_KEY] && typeof changes[RAIL_SETTING_KEY].newValue === 'boolean') {
      callback(changes[RAIL_SETTING_KEY].newValue);
    }
  };
  s.onChanged.addListener(listener);
  return () => s.onChanged.removeListener(listener);
}
